import { ModelBase } from '../../core/model/model-base';
import { Dwelling } from '../../rent/model/dwelling';
import { SelectionItem } from '../../core/component/select/select.component';
import { DwellingInspectionRoom, RoomType } from './dwelling-inspection-room';
import { LocalDate } from '../../core/model/local-date';
import { InvoiceDeliveryConfiguration } from '../../invoice/model/invoice-delivery-configuration';
import { DwellingInspectionKey } from './dwelling-inspection-key';
import { DwellingInspectionInvoicePoint } from './dwelling-inspection-invoice-point';
import { AutoCompleteModel } from '../../core/component/autocomplete/autocomplete-model';
import { LeaseAgreementStatus } from '../../rent/model/const/enums';
import { WebUser } from '../../common/model/webUser';
import { ExecutorGroup } from '../../common/model/executor-group';

export const dwellingInspectionCommand = {
  print: 'print',
  openDwelling: 'openDwelling',
};

export enum DwellingInspectionStatus {
  Cancelled = 0,
  Completed = 1,
  Registered = 2,
}

export function DwellingInspectionStatusToString(status: DwellingInspectionStatus): string {
  switch (status) {
    case DwellingInspectionStatus.Cancelled:
      return 'DWELLING_INSPECTION.STATUS.CANCELLED';

    case DwellingInspectionStatus.Completed:
      return 'DWELLING_INSPECTION.STATUS.COMPLETED';

    case DwellingInspectionStatus.Registered:
      return 'DWELLING_INSPECTION.STATUS.REGISTERED';

    default:
      throw new Error('Unsupported status.');
  }
}

export type ContractItem = {
    id: string;
    rentalObjectId: string;
    tenantOneId: string;
    tenantOneName: string;
    tenantOnePhone: string;
    tenantTwoId: string;
    tenantTwoName: string;
    tenantTwoPhone: string;
    streetAddress: string;
    leaseAgreementNumber: string;
    description: string;
    fromDate: LocalDate;
    lastBillingDate: LocalDate;
    status: LeaseAgreementStatus;
  };

export class DwellingInspection extends ModelBase {
  constructor(dataContext: any) {
    super();
    this.id = dataContext.id;
    this.address = dataContext.address || '';
    this.approvedByNewTenant = dataContext.approvedByNewTenant;
    this.approvedByTenant = dataContext.approvedByTenant;
    this.cleaned = dataContext.cleaned;
    this.companyName = dataContext.companyName || '';
    this.dwelling = dataContext.dwelling || '';
    this.dwellingNumber = dataContext.dwellingNumber || '';
    this.dwellingId = dataContext.dwellingId || '';
    this.dwellingType = dataContext.dwellingType || '';
    this.inspector = dataContext.inspector || '';
    this.inspectorName = dataContext.inspectorName || '';
    this.inspectors = Array.isArray(dataContext.inspectors) ? dataContext.inspectors.map(x => new AutoCompleteModel(x.name, x.id)) : [];
    // V2 keeps the relation id and the display name separate on the detail response:
    // dwellingInspectionType is the Guid used for updates, while
    // dwellingInspectionTypeName is what the user should see. List responses only
    // expose the display name under the legacy dwellingInspectionType field, so keep
    // supporting that read-only shape as well.
    const hasDwellingInspectionTypeName = Object.prototype.hasOwnProperty.call(dataContext, 'dwellingInspectionTypeName');
    this.dwellingInspectionTypeId = hasDwellingInspectionTypeName ? dataContext.dwellingInspectionType || '' : '';
    this.dwellingInspectionType = hasDwellingInspectionTypeName
      ? dataContext.dwellingInspectionTypeName || ''
      : dataContext.dwellingInspectionType || '';
    this.dwellingInspectionTypes = Array.isArray(dataContext.dwellingInspectionTypes)
      ? dataContext.dwellingInspectionTypes.map(x => new SelectionItem(x.name, x.id))
      : [];
    this.inspectionDate = LocalDate.fromDate(dataContext.inspectionDate);
    this.inspectionTime = dataContext.inspectionTime;
    this.invoiceDeliveryConfiguration = new InvoiceDeliveryConfiguration(dataContext.invoiceDeliveryConfiguration);
    this.keys = Array.isArray(dataContext.keys) ? dataContext.keys.map(k => new DwellingInspectionKey(k)) : [];
    this.invoicePoints = Array.isArray(dataContext.invoicePoints)
      ? dataContext.invoicePoints.map(k => new DwellingInspectionInvoicePoint(k))
      : [];
    this.newTenant = dataContext.newTenant;
    this.newTenants = Array.isArray(dataContext.newTenants) ? dataContext.newTenants.map(x => new SelectionItem(x.name, x.id)) : [];
    this.newTenantDemagesNote = dataContext.newTenantDemagesNote || '';
    this.note = dataContext.note || '';
    this.inspectionNote = dataContext.inspectionNote || '';
    this.number = dataContext.number ? dataContext.number : 1;
    this.priceListId = dataContext.priceListId;
    this.property = dataContext.property || '';
    this.rentalId = dataContext.rentalId || '';
    this.repairDeadline = LocalDate.fromDate(dataContext.repairDeadline);
    this.repairNote = dataContext.repairNote || '';
    this.entrance = dataContext.entrance || null;
    this.status = dataContext.status;
    this.surface = dataContext.surface || null;
    this.tenant = dataContext.tenant;
    this.tenants = Array.isArray(dataContext.tenants) ? dataContext.tenants.map(x => new SelectionItem(x.name, x.id)) : [];
    this.tenantPaymentNote = dataContext.tenantPaymentNote || '';
    this.currencyCode = dataContext.currencyCode;
    this.newTenantSignature = dataContext.newTenantSignature;
    this.tenantSignature = dataContext.tenantSignature;
    this.movingOutTenantOneEmail = dataContext.movingOutTenantOneEmail;
    this.movingOutTenantOneName = dataContext.movingOutTenantOneName;
    this.movingOutTenantOnePhone = dataContext.movingOutTenantOnePhone;
    this.movingOutTenantLeaseAgreementNumber = dataContext.movingOutTenantLeaseAgreementNumber;
    this.tenantMovingOutDate = LocalDate.fromDate(dataContext.tenantMovingOutDate);
    this.rooms = Array.isArray(dataContext.rooms) ? dataContext.rooms.map(x => new DwellingInspectionRoom(x)) : [];
    this.updateRoomIndexes();
    this.contracts = dataContext.contracts;
    this.leaseAgreement = dataContext.leaseAgreement;
    this.newLeaseAgreement = dataContext.newLeaseAgreement;
    this.propertyId = dataContext.propertyId;
    this.faultNotificationExecutorWebUserId = dataContext.faultNotificationExecutorWebUserId;
    this.faultNotificationExecutorGroupId = dataContext.faultNotificationExecutorGroupId;
    this.faultNotificationRecipientId = dataContext.faultNotificationRecipientId;
  }

  address: string;
  approvedByNewTenant: boolean;
  approvedByTenant: boolean;
  cleaned: boolean;
  currencyCode: string;
  companyName: string;
  tenantMovingOutDate: LocalDate;
  movingOutTenantOneEmail: string;
  movingOutTenantOneName: string;
  movingOutTenantOnePhone: string;
  movingOutTenantLeaseAgreementNumber: string
  dwelling: string | Dwelling;
  dwellingId: string;
  dwellingNumber: string;
  dwellingType: string;
  inspector: string;
  inspectorName: string;
  inspectors: AutoCompleteModel[];
  /** Display name used by inspection lists and the inspection detail form. */
  dwellingInspectionType: string;
  /** Guid used by the inspection detail form's PUT request. */
  dwellingInspectionTypeId: string;
  dwellingInspectionTypes: SelectionItem[];
  inspectionDate?: LocalDate;
  inspectionTime: string;
  get inspectionTimeWithoutSeconds(): string {
    // Assumes format "HH:MM:SS" or "H:M:S"
    if (!this.inspectionTime) return null;
    const [hours, minutes] = this.inspectionTime.split(":");
    return `${hours}:${minutes}`;
  }
  invoiceDeliveryConfiguration: InvoiceDeliveryConfiguration;
  get isCompleted(): boolean {
    return this.status === DwellingInspectionStatus.Completed;
  }
  get isRegistered(): boolean {
    return this.status === DwellingInspectionStatus.Registered;
  }
  keys: DwellingInspectionKey[];
  invoicePoints: DwellingInspectionInvoicePoint[];
  leaseAgreement: string;
  newLeaseAgreement: string;
  newTenant: string;
  newTenantDemagesNote: string;
  newTenants: SelectionItem[];
  newTenantSignature: string;
  note: string;
  inspectionNote: string;
  get inspectionNoteShort(): string {
    return this.inspectionNote.length <= 50 ? this.inspectionNote : this.inspectionNote?.slice(0, 50) + '...';
  }
  number: number;
  rentalId: string;
  repairDeadline: LocalDate;
  repairNote: string;
  rooms: DwellingInspectionRoom[];
  entrance: string;
  status: DwellingInspectionStatus;
  get statusCaption(): string {
    return DwellingInspectionStatusToString(this.status);
  }
  surface: number;
  tenant: string;
  priceListId: string;
  tenantPaymentNote: string;
  tenants: SelectionItem[];
  tenantSignature: string;
  contracts: ContractItem[];

  property: string;
  propertyId: string;
  faultNotificationExecutorWebUserId: string; // Property default value
  faultNotificationExecutorGroupId: string; // Property default value
  faultNotificationRecipientId: string; // Property default value

  toJSON() {
    const json = {
      approvedByNewTenant: this.approvedByNewTenant,
      approvedByTenant: this.approvedByTenant,
      cleaned: this.cleaned,
      dwelling: this.dwelling,
      id: this.id,
      // Optional relations must be null when empty. V2 accepts null as the
      // default Guid value, whereas an empty string is not a valid Guid token.
      inspector: this.inspector || null,
      // The update API binds this field as a Guid. Never send the display name
      // (or the empty form-control value) back as dwellingInspectionType.
      dwellingInspectionType: this.dwellingInspectionTypeId || null,
      inspectionDate: this.inspectionDate,
      inspectionTime: this.inspectionTime,
      invoiceDeliveryConfiguration: this.invoiceDeliveryConfiguration,
      keys: this.keys,
      leaseAgreement: this.leaseAgreement,
      newLeaseAgreement: this.newLeaseAgreement,
      newTenant: this.newTenant,
      newTenantDemagesNote: this.newTenantDemagesNote,
      note: this.note,
      inspectionNote: this.inspectionNote,
      priceListId: this.priceListId,
      repairDeadline: this.repairDeadline,
      repairNote: this.repairNote,
      tenant: this.tenant,
      tenantPaymentNote: this.tenantPaymentNote,
    };

    if (this.dwelling instanceof Dwelling) {
      json.dwelling = this.dwelling.id;
    }
    return json;
  }

  toString(culture?: string): string {
    const template = DwellingInspection._translations[culture]?.DELETE_INSPECTION_TEXT
      || DwellingInspection._translations.sv.DELETE_INSPECTION_TEXT;

    return template
      .replace('{inspectionNumber}', this.number.toString())
      .replace('{address}', this.address);
  }

  private static _translations: Record<string, Record<string, string>> = {
    en: { DELETE_INSPECTION_TEXT: 'Inspection #{inspectionNumber}, {address}' },
    sv: { DELETE_INSPECTION_TEXT: 'Inspektion #{inspectionNumber}, {address}' },
    fi: { DELETE_INSPECTION_TEXT: 'Tarkastus #{inspectionNumber}, {address}' },
    da: { DELETE_INSPECTION_TEXT: 'Inspektion #{inspectionNumber}, {address}' },
  };

  countRooms(type: RoomType): number {
    let count = 0;
    for (let index = 0; index < this.rooms.length; index++) {
      let room = this.rooms[index];
      if (room.type === type) count++;
    }
    return count;
  }

  updateRoomIndexes(types?: RoomType[]) {
    if (!this.rooms) {
      return;
    }

    if (!types) {
      types = [
        RoomType.Bedroom,
        RoomType.Bathroom,
        RoomType.DressingRoom,
        RoomType.Hall,
        RoomType.Kitchen,
        RoomType.LivingRoom,
        RoomType.Toilet,
        RoomType.Balcony,
        RoomType.Patio,
        RoomType.Other,
      ];
    }

    types.forEach(type => {
      const items = this.rooms.filter(x => x.type === type);
      if (items.length > 1) {
        let index = 1;
        items.forEach(x => {
          x.inspectionOrder = index;
          index++;
        });
      } else if (items.length == 1) {
        items[0].inspectionOrder = 0;
      }
    });
  }

  // Selection items
  resources: WebUser[] = [];
  executorGroups: ExecutorGroup[] = [];
  webUsers: WebUser[] = [];
}
